import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import i18next from 'i18next'
import { financialService } from '@/services/api'
import { queryKeys } from '@/config/queryKeys'
import type { GetTransactionsParams } from '@/services/api/financial/financial.static'
import type { GetFinancialSummaryParams } from '@/services/api/financial/financial.static'

export const useTransactions = (params?: GetTransactionsParams) => {
  return useQuery({
    queryKey: queryKeys.financials.list({ ...params }),
    queryFn: () => financialService.getTransactions(params),
  })
}

export const useTransaction = (id: string) => {
  return useQuery({
    queryKey: queryKeys.financials.detail(id),
    queryFn: () => financialService.getTransaction(id),
    enabled: !!id,
  })
}

export const useCreateTransaction = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: financialService.createTransaction,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.financials.lists() })
      queryClient.invalidateQueries({ queryKey: queryKeys.financials.all })
      toast.success(i18next.t('financials:transactionCreated'))
    },
    onError: () => {
      toast.error(i18next.t('financials:form.errors.createFailed'))
    },
  })
}

export const useUpdateTransaction = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: Parameters<typeof financialService.updateTransaction>[1]
    }) => financialService.updateTransaction(id, data),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.financials.lists() })
      queryClient.invalidateQueries({ queryKey: queryKeys.financials.detail(id) })
      queryClient.invalidateQueries({ queryKey: queryKeys.financials.all })
      toast.success(i18next.t('financials:transactionUpdated'))
    },
    onError: () => {
      toast.error(i18next.t('common:errors.saveFailed'))
    },
  })
}

export const useDeleteTransaction = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => financialService.deleteTransaction(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.financials.lists() })
      queryClient.invalidateQueries({ queryKey: queryKeys.financials.all })
      toast.success(i18next.t('financials:transactionDeleted'))
    },
    onError: () => {
      toast.error(i18next.t('common:errors.saveFailed'))
    },
  })
}

export const useFinancialSummary = (params?: GetFinancialSummaryParams) => {
  return useQuery({
    queryKey: queryKeys.financials.summary(JSON.stringify(params)),
    queryFn: () => financialService.getSummary(params),
  })
}
